import { useCallback, useEffect, useMemo, useState } from 'react';

import http from 'services/http';
import { alerta } from '../../../components/Alert';

import styles from './styles.module.css';

// O backend normaliza milhar com str_pad/substr para 4 dígitos e grupo com (int)
// no CalculaResultado. A normalização daqui espelha isso para o que é gravado
// bater com o que é comparado na apuração.
const CONFIG = {
    milhar: {
        singular: 'milhar',
        plural: 'milhares',
        genero: 'f',
        placeholder: 'Ex: 1224',
        maxLength: 4,
        largura: 120,
        vazio: 'Nenhuma milhar rebaixada',
        chaveNumeros: 'milhar_rebaixada_numeros',
        chavePorcentagem: 'milhar_rebaixada_porcentagem',
        normalizar: (bruto) => bruto.padStart(4, '0').slice(-4),
        validar: (bruto) =>
            /^\d{1,4}$/.test(bruto) ? null : 'Milhar inválida. Digite de 1 a 4 dígitos.',
    },
    grupo: {
        singular: 'grupo',
        plural: 'grupos',
        genero: 'm',
        placeholder: 'Ex: 14',
        maxLength: 2,
        largura: 100,
        vazio: 'Nenhum grupo rebaixado',
        chaveNumeros: 'grupo_rebaixado_numeros',
        chavePorcentagem: 'grupo_rebaixado_porcentagem',
        normalizar: (bruto) => String(parseInt(bruto, 10)),
        validar: (bruto) => {
            if (!/^\d{1,2}$/.test(bruto)) return 'Grupo inválido. Digite apenas dígitos.';
            const numero = parseInt(bruto, 10);
            if (numero < 1 || numero > 25) return 'Grupo inválido. Use um número de 1 a 25.';
            return null;
        },
    },
};

const ordenar = (numeros) => [...numeros].sort((a, b) => Number(a) - Number(b));

// Só para particípios variáveis em gênero: 'adicionad' -> "2 milhares adicionadas".
const conjugar = (cfg, quantidade, base) => {
    const genero = cfg.genero === 'f' ? 'a' : 'o';
    const plural = quantidade === 1 ? '' : 's';
    return `${quantidade} ${quantidade === 1 ? cfg.singular : cfg.plural} ${base}${genero}${plural}`;
};

const contar = (cfg, quantidade) =>
    `${quantidade} ${quantidade === 1 ? cfg.singular : cfg.plural}`;

const artigo = (cfg) => (cfg.genero === 'f' ? 'A' : 'O');
const artigoContraido = (cfg) => (cfg.genero === 'f' ? 'da' : 'do');

// O alerta() só emite toast quando o payload tem `errors` ou `success`; um objeto
// solto com `message` passa batido. Toda mensagem daqui sai por estes dois helpers.
const sucesso = (message) => alerta({ success: true, message });
const falha = (message) => alerta({ success: false, message });

const mensagemDoErro = (e, padrao) => e?.response?.data?.message ?? padrao;

function useListaRebaixada(cfg) {
    const [porcentagem, setPorcentagem] = useState('0');
    const [porcentagemSalva, setPorcentagemSalva] = useState('0');
    const [salvos, setSalvos] = useState([]);
    const [carrinho, setCarrinho] = useState([]);
    const [removidos, setRemovidos] = useState([]);
    const [entrada, setEntrada] = useState('');
    const [busca, setBusca] = useState('');

    const definirCarregado = useCallback((numeros, pct) => {
        setSalvos(Array.isArray(numeros) ? numeros.map(String) : []);
        setPorcentagem(String(pct ?? '0'));
        setPorcentagemSalva(String(pct ?? '0'));
        setCarrinho([]);
        setRemovidos([]);
    }, []);

    const adicionar = () => {
        const bruto = entrada.trim();

        const erro = cfg.validar(bruto);
        if (erro) {
            falha(erro);
            return;
        }

        const valor = cfg.normalizar(bruto);
        setEntrada('');

        if (carrinho.includes(valor)) {
            falha(`${artigo(cfg)} ${cfg.singular} ${valor} já está no carrinho.`);
            return;
        }

        if (salvos.includes(valor)) {
            if (removidos.includes(valor)) {
                setRemovidos(removidos.filter((n) => n !== valor));
                sucesso(`Remoção ${artigoContraido(cfg)} ${cfg.singular} ${valor} desfeita.`);
            } else {
                falha(`${artigo(cfg)} ${cfg.singular} ${valor} já está na lista de salvos.`);
            }
            return;
        }

        setCarrinho([...carrinho, valor]);
    };

    const removerDoCarrinho = (valor) => {
        setCarrinho(carrinho.filter((n) => n !== valor));
        sucesso(`${artigo(cfg)} ${cfg.singular} ${valor} saiu do carrinho.`);
    };

    const alternarRemocao = (valor) => {
        if (removidos.includes(valor)) {
            setRemovidos(removidos.filter((n) => n !== valor));
            sucesso(`Remoção ${artigoContraido(cfg)} ${cfg.singular} ${valor} desfeita.`);
            return;
        }
        setRemovidos([...removidos, valor]);
        falha(
            `${artigo(cfg)} ${cfg.singular} ${valor} será removid${cfg.genero === 'f' ? 'a' : 'o'} ao salvar.`
        );
    };

    const salvosOrdenados = useMemo(() => ordenar(salvos), [salvos]);

    const salvosVisiveis = useMemo(() => {
        const termo = busca.trim();
        return termo ? salvosOrdenados.filter((n) => n.includes(termo)) : salvosOrdenados;
    }, [salvosOrdenados, busca]);

    // O que vai para a API: os salvos que sobraram mais o carrinho.
    const listaFinal = useMemo(
        () => ordenar([...salvos.filter((n) => !removidos.includes(n)), ...carrinho]),
        [salvos, removidos, carrinho]
    );

    const qtdAlteracoes =
        carrinho.length + removidos.length + (porcentagem !== porcentagemSalva ? 1 : 0);

    const aplicarSalvamento = (lista, pct) => {
        setSalvos(lista);
        setCarrinho([]);
        setRemovidos([]);
        // Reflete no campo o valor realmente enviado: um campo esvaziado vira '0' no
        // payload, e sem isso ele ficaria marcado como pendente para sempre.
        setPorcentagem(pct);
        setPorcentagemSalva(pct);
    };

    return {
        cfg,
        porcentagem,
        setPorcentagem,
        salvos,
        carrinho,
        removidos,
        entrada,
        setEntrada,
        busca,
        setBusca,
        salvosVisiveis,
        listaFinal,
        qtdAlteracoes,
        definirCarregado,
        adicionar,
        removerDoCarrinho,
        alternarRemocao,
        aplicarSalvamento,
    };
}

function Painel({ lista }) {
    const { cfg } = lista;
    const totalSalvos = lista.salvos.length;
    const buscando = lista.busca.trim() !== '';

    return (
        <div className={styles.painel}>
            <div className={styles.linhaPorcentagem}>
                <label className={styles.rotuloPorcentagem}>Porcentagem do prêmio (%):</label>
                <input
                    type="number"
                    inputMode="numeric"
                    className="form-control"
                    style={{ width: 100 }}
                    value={lista.porcentagem}
                    onChange={(e) =>
                        lista.setPorcentagem(e.target.value.replace(/\D/g, '').slice(0, 2))
                    }
                />
            </div>

            <div className={styles.adicionarLinha}>
                <input
                    type="text"
                    inputMode="numeric"
                    maxLength={cfg.maxLength}
                    className="form-control"
                    style={{ width: cfg.largura }}
                    placeholder={cfg.placeholder}
                    value={lista.entrada}
                    onChange={(e) =>
                        lista.setEntrada(e.target.value.replace(/\D/g, '').slice(0, cfg.maxLength))
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            lista.adicionar();
                        }
                    }}
                />
                <button className="btn btn-primary" onClick={lista.adicionar} type="button">
                    Adicionar
                </button>
            </div>

            <section className={styles.bloco}>
                <header className={styles.blocoCabecalho}>
                    <span className={styles.blocoTitulo}>A adicionar</span>
                    <span className={styles.blocoContador}>
                        {lista.carrinho.length === 0
                            ? 'carrinho vazio'
                            : `${contar(cfg, lista.carrinho.length)} no carrinho`}
                    </span>
                </header>

                <div className={`${styles.listaNumeros} ${styles.listaCarrinho}`}>
                    {lista.carrinho.length === 0 ? (
                        <div className={styles.vazio}>
                            Digite {cfg.genero === 'f' ? 'uma' : 'um'} {cfg.singular} acima e clique
                            em Adicionar.
                        </div>
                    ) : (
                        lista.carrinho.map((n) => (
                            <span key={n} className={styles.chip}>
                                {n}
                                <button
                                    className={styles.chipRemover}
                                    onClick={() => lista.removerDoCarrinho(n)}
                                    type="button"
                                    title="Tirar do carrinho"
                                >
                                    &times;
                                </button>
                            </span>
                        ))
                    )}
                </div>

                {lista.carrinho.length > 0 && (
                    <p className={styles.dica}>
                        {cfg.genero === 'f' ? 'Estas' : 'Estes'} {cfg.plural} só
                        {cfg.genero === 'f' ? ' serão gravadas' : ' serão gravados'} ao clicar em
                        Salvar.
                    </p>
                )}
            </section>

            <section className={styles.bloco}>
                <header className={styles.blocoCabecalho}>
                    <span className={styles.blocoTitulo}>Salvos</span>
                    {totalSalvos > 0 && (
                        <input
                            type="text"
                            inputMode="numeric"
                            className={`form-control ${styles.campoBusca}`}
                            placeholder="Filtrar..."
                            value={lista.busca}
                            onChange={(e) =>
                                lista.setBusca(
                                    e.target.value.replace(/\D/g, '').slice(0, cfg.maxLength)
                                )
                            }
                        />
                    )}
                    <span className={styles.blocoContador}>
                        {buscando
                            ? `${lista.salvosVisiveis.length} de ${totalSalvos}`
                            : contar(cfg, totalSalvos)}
                    </span>
                </header>

                <div className={styles.listaNumeros}>
                    {totalSalvos === 0 ? (
                        <div className={styles.vazio}>{cfg.vazio}</div>
                    ) : lista.salvosVisiveis.length === 0 ? (
                        <div className={styles.vazio}>
                            Nenhum resultado para "{lista.busca.trim()}".
                        </div>
                    ) : (
                        lista.salvosVisiveis.map((n) => {
                            const marcado = lista.removidos.includes(n);
                            return (
                                <span
                                    key={n}
                                    className={`${styles.chipSalvo} ${marcado ? styles.chipRemovido : ''}`}
                                >
                                    {n}
                                    <button
                                        className={styles.chipAcao}
                                        onClick={() => lista.alternarRemocao(n)}
                                        type="button"
                                        title={marcado ? 'Desfazer remoção' : 'Marcar para remover'}
                                    >
                                        {marcado ? '↩' : '×'}
                                    </button>
                                </span>
                            );
                        })
                    )}
                </div>

                {lista.removidos.length > 0 && (
                    <p className={styles.dicaAlerta}>
                        {conjugar(cfg, lista.removidos.length, 'marcad')} para remoção. A remoção só
                        acontece ao clicar em Salvar.
                    </p>
                )}
            </section>
        </div>
    );
}

export default function MilharBichoRebaixado() {
    const [tab, setTab] = useState('milhar');
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);

    const milhar = useListaRebaixada(CONFIG.milhar);
    const grupo = useListaRebaixada(CONFIG.grupo);

    const { definirCarregado: carregarMilhar } = milhar;
    const { definirCarregado: carregarGrupo } = grupo;

    useEffect(() => {
        const carregar = async () => {
            try {
                const { data } = await http.get('/bancas/opcoes');
                const opcoes = data?.data?.opcoes ?? {};

                carregarMilhar(
                    opcoes.milhar_rebaixada_numeros,
                    opcoes.milhar_rebaixada_porcentagem
                );
                carregarGrupo(opcoes.grupo_rebaixado_numeros, opcoes.grupo_rebaixado_porcentagem);
            } catch (e) {
                falha(mensagemDoErro(e, 'Erro ao carregar a configuração.'));
            } finally {
                setCarregando(false);
            }
        };

        carregar();
    }, [carregarMilhar, carregarGrupo]);

    const totalAlteracoes = milhar.qtdAlteracoes + grupo.qtdAlteracoes;

    const salvar = async () => {
        // Fixa o que vai ser enviado antes do await para o resumo bater com o payload.
        const listaMilhar = milhar.listaFinal;
        const listaGrupo = grupo.listaFinal;
        const pctMilhar = milhar.porcentagem || '0';
        const pctGrupo = grupo.porcentagem || '0';

        const resumo = [
            milhar.carrinho.length && conjugar(CONFIG.milhar, milhar.carrinho.length, 'adicionad'),
            milhar.removidos.length && conjugar(CONFIG.milhar, milhar.removidos.length, 'removid'),
            grupo.carrinho.length && conjugar(CONFIG.grupo, grupo.carrinho.length, 'adicionad'),
            grupo.removidos.length && conjugar(CONFIG.grupo, grupo.removidos.length, 'removid'),
        ].filter(Boolean);

        try {
            setSalvando(true);

            // JSON, e não FormData: o AtualizaOpcoesService faz merge raso por chave
            // ($banca->opcoes = [...$banca->opcoes, ...$dados]), então uma lista vazia
            // precisa chegar como chave presente com array vazio. Com FormData + append
            // por item a chave sumia do payload e o backend mantinha a lista antiga.
            await http.post('/bancas/opcoes', {
                [CONFIG.milhar.chavePorcentagem]: pctMilhar,
                [CONFIG.milhar.chaveNumeros]: listaMilhar,
                [CONFIG.grupo.chavePorcentagem]: pctGrupo,
                [CONFIG.grupo.chaveNumeros]: listaGrupo,
            });

            milhar.aplicarSalvamento(listaMilhar, pctMilhar);
            grupo.aplicarSalvamento(listaGrupo, pctGrupo);

            sucesso(
                resumo.length ? `Salvo: ${resumo.join(', ')}.` : 'Configuração salva com sucesso!'
            );
        } catch (e) {
            falha(mensagemDoErro(e, 'Erro ao salvar a configuração.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <>
            <div className="titulo-principal">Milhar/Grupo Rebaixado</div>
            <div className={styles.conteudoPrincipal}>
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${tab === 'milhar' ? styles.tabAtiva : ''}`}
                        onClick={() => setTab('milhar')}
                        type="button"
                    >
                        Milhar
                        {milhar.qtdAlteracoes > 0 && (
                            <span className={styles.selo}>{milhar.qtdAlteracoes}</span>
                        )}
                    </button>
                    <button
                        className={`${styles.tab} ${tab === 'grupo' ? styles.tabAtiva : ''}`}
                        onClick={() => setTab('grupo')}
                        type="button"
                    >
                        Grupo
                        {grupo.qtdAlteracoes > 0 && (
                            <span className={styles.selo}>{grupo.qtdAlteracoes}</span>
                        )}
                    </button>
                </div>

                {carregando ? (
                    <div className={styles.painel}>
                        <div className="loader" />
                    </div>
                ) : (
                    <Painel lista={tab === 'milhar' ? milhar : grupo} />
                )}

                <div className={styles.rodape}>
                    {totalAlteracoes > 0 && (
                        <span className={styles.resumoPendente}>
                            {totalAlteracoes === 1
                                ? '1 alteração pendente'
                                : `${totalAlteracoes} alterações pendentes`}
                        </span>
                    )}
                    <button
                        className="btn btn-success"
                        style={{ minWidth: 200 }}
                        onClick={salvar}
                        disabled={salvando || carregando}
                        type="button"
                    >
                        {salvando
                            ? 'Salvando...'
                            : totalAlteracoes === 0
                              ? 'Salvar'
                              : totalAlteracoes === 1
                                ? 'Salvar 1 alteração'
                                : `Salvar ${totalAlteracoes} alterações`}
                    </button>
                </div>
            </div>
        </>
    );
}
